// Custom reports data layer — the flat per-guest dataset (columns catalog +
// rows, all slicing client-side), saved_reports CRUD (generated routes), and
// the AI view-spec generator. The spec contract lives server-side
// (quickback/features/reports/lib/report-spec.ts); this mirror is deliberately
// lenient (column: string) because column ids are per-event — the UI prunes
// unknown ids against the dataset's catalog instead.

import { z } from "zod"
import { api } from "./api"
import { listAllData } from "./list-all"

export type ReportColumnType = "string" | "number" | "boolean" | "date" | "enum"

export type ReportColumnMeta = {
  id: string
  label: string
  type: ReportColumnType
  options?: string[]
}

export const FILTER_OPS = [
  "eq",
  "neq",
  "contains",
  "is_empty",
  "not_empty",
  "in",
  "gt",
  "lt",
] as const

export type ReportFilterOp = (typeof FILTER_OPS)[number]

export type ReportFilter = {
  column: string
  op: ReportFilterOp
  value?: string | number | boolean | string[]
}

export type ReportSpec = {
  columns: string[]
  filters: ReportFilter[]
  groupBy: string | null
  sort: { column: string; dir: "asc" | "desc" } | null
}

export type ReportRow = { id: string } & Record<string, string | number | boolean | null>

export type ReportDataset = {
  columns: ReportColumnMeta[]
  aiEnabled: boolean
  total: number
  rows: ReportRow[]
}

export type SavedReport = {
  id: string
  eventId: string
  name: string
  spec: string
  modifiedAt: string
}

const specSchema = z.object({
  columns: z.array(z.string()).min(1),
  filters: z
    .array(
      z.object({
        column: z.string(),
        op: z.enum(FILTER_OPS),
        value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]).optional(),
      }),
    )
    .default([]),
  groupBy: z.string().nullable().default(null),
  sort: z.object({ column: z.string(), dir: z.enum(["asc", "desc"]) }).nullable().default(null),
})

export function parseSpec(raw: string): ReportSpec | null {
  try {
    const parsed = specSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

// Drop anything referencing columns the current dataset doesn't have (deleted
// custom field, removed form) so a stale saved report still renders.
export function sanitizeSpec(spec: ReportSpec, columns: ReportColumnMeta[]): ReportSpec {
  const known = new Set(columns.map((c) => c.id))
  const cols = spec.columns.filter((c) => known.has(c))
  return {
    columns: cols.length ? cols : DEFAULT_SPEC.columns.filter((c) => known.has(c)),
    filters: spec.filters.filter((f) => known.has(f.column)),
    groupBy: spec.groupBy && known.has(spec.groupBy) ? spec.groupBy : null,
    sort: spec.sort && known.has(spec.sort.column) ? spec.sort : null,
  }
}

export const DEFAULT_SPEC: ReportSpec = {
  columns: ["displayName", "email", "status", "company", "ticketType"],
  filters: [],
  groupBy: null,
  sort: { column: "displayName", dir: "asc" },
}

export function reportDatasetQueryOptions(eventId: string) {
  return {
    queryKey: ["report-dataset", eventId] as const,
    queryFn: () =>
      api<ReportDataset>("/api/v1/reports/dataset", { method: "POST", body: { eventId } }),
    staleTime: 30_000,
  }
}

export function savedReportsQueryOptions(eventId: string) {
  return {
    queryKey: ["resource", "saved-reports", { eventId }] as const,
    queryFn: () =>
      listAllData<SavedReport>("/api/v1/saved-reports", { eventId, sort: "name", order: "asc" }),
    select: (r: { data: SavedReport[] }) => r.data ?? [],
  }
}

export async function createSavedReport(input: {
  eventId: string
  name: string
  spec: ReportSpec
}): Promise<SavedReport> {
  return api<SavedReport>("/api/v1/saved-reports", {
    method: "POST",
    body: { eventId: input.eventId, name: input.name.trim(), spec: JSON.stringify(input.spec) },
  })
}

export async function updateSavedReport(
  id: string,
  patch: { name?: string; spec?: ReportSpec },
): Promise<SavedReport> {
  return api<SavedReport>(`/api/v1/saved-reports/${id}`, {
    method: "PATCH",
    body: {
      ...(patch.name != null ? { name: patch.name.trim() } : {}),
      ...(patch.spec != null ? { spec: JSON.stringify(patch.spec) } : {}),
    },
  })
}

export async function deleteSavedReport(id: string): Promise<void> {
  await api(`/api/v1/saved-reports/${id}`, { method: "DELETE" })
}

export async function generateReportSpec(input: {
  eventId: string
  prompt: string
  currentSpec?: ReportSpec
}): Promise<ReportSpec> {
  const r = await api<{ spec: ReportSpec }>("/api/v1/reports/generate-spec", {
    method: "POST",
    body: input,
  })
  return r.spec
}
